import React, {useEffect, useMemo, useState} from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ButtonExit from "../../../../components/UI/button/ButtonExit";
import ButtonAdd from "../../../../components/UI/button/ButtonAdd";
import {DEFAULT_DXF_THICKNESS_MM} from "./dxf/parseDxfToDetailData";

const HOLES_THROUGH = "through";
const HOLES_BLIND = "blind";

/** Глубина отверстий по режиму: сквозные = толщина, глухие < толщины. */
export const resolveHoleDepthMm = (heightMm, holesThrough, holeDepthMm) => {
    const height = Math.max(Number(heightMm) || DEFAULT_DXF_THICKNESS_MM, 1);
    if (holesThrough) return height;
    const maxBlind = Math.max(height - 1, 0.1);
    const raw = Number(holeDepthMm);
    const preferred = Number.isFinite(raw) && raw > 0 ? raw : Math.min(height * 0.5, maxBlind);
    return Math.min(Math.max(preferred, 0.1), maxBlind);
};

export const applyHolesDepth = (holes, heightMm, holesThrough, holeDepthMm) => {
    const depth = resolveHoleDepthMm(heightMm, holesThrough, holeDepthMm);
    return (holes || []).map((hole) => ({...hole, depth}));
};

const inferHolesMode = (initialValues) => {
    if (typeof initialValues?.holesThrough === "boolean") {
        return initialValues.holesThrough ? HOLES_THROUGH : HOLES_BLIND;
    }
    const height = Number(initialValues?.heightMm) || DEFAULT_DXF_THICKNESS_MM;
    const depth = Number(initialValues?.holeDepthMm);
    if (!Number.isFinite(depth)) return HOLES_THROUGH;
    return depth >= height - 0.5 ? HOLES_THROUGH : HOLES_BLIND;
};

/**
 * Импорт DXF или редактирование детали:
 * позиция, имя, толщина, режим отверстий.
 */
const DxfImportDialog = ({
    open,
    onClose,
    onConfirm,
    fileName = "",
    meta = null,
    parseError = null,
    busy = false,
    mode = "import",
    initialValues = null,
}) => {
    const isEdit = mode === "edit";
    const [position, setPosition] = useState("");
    const [name, setName] = useState("");
    const [heightMm, setHeightMm] = useState(String(DEFAULT_DXF_THICKNESS_MM));
    const [heightError, setHeightError] = useState("");
    const [holesMode, setHolesMode] = useState(HOLES_THROUGH);
    const [holeDepthMm, setHoleDepthMm] = useState("");
    const [holeDepthError, setHoleDepthError] = useState("");

    useEffect(() => {
        if (!open) return;
        setHeightError("");
        setHoleDepthError("");
        if (isEdit) {
            const height = Number(initialValues?.heightMm) || DEFAULT_DXF_THICKNESS_MM;
            setPosition(String(initialValues?.position ?? ""));
            setName(String(initialValues?.name ?? "") || "DXF деталь");
            setHeightMm(String(height));
            const modeValue = inferHolesMode(initialValues);
            setHolesMode(modeValue);
            const blindDefault = Math.min(height * 0.5, Math.max(height - 1, 0.1));
            const depthInit = Number(initialValues?.holeDepthMm);
            setHoleDepthMm(String(
                modeValue === HOLES_BLIND && Number.isFinite(depthInit)
                    ? depthInit
                    : blindDefault,
            ));
            return;
        }
        const base = String(fileName || "")
            .replace(/\.[^.]+$/, "")
            .trim();
        setPosition("");
        setName(base || "DXF деталь");
        setHeightMm(String(DEFAULT_DXF_THICKNESS_MM));
        setHolesMode(HOLES_THROUGH);
        setHoleDepthMm(String(DEFAULT_DXF_THICKNESS_MM * 0.5));
        // initialValues читаем только при открытии
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fileName, isEdit]);

    const previewMeta = meta || (isEdit && initialValues?.length != null
        ? {
            length: initialValues.length,
            width: initialValues.width,
            holeCount: initialValues.holeCount,
            contourSource: initialValues.contourSource,
        }
        : null);

    const holeCount = useMemo(() => {
        if (previewMeta?.holeCount != null) return Number(previewMeta.holeCount) || 0;
        return Number(initialValues?.holeCount) || 0;
    }, [previewMeta, initialValues]);

    const hasHoles = holeCount > 0;

    const handleClose = () => {
        if (busy) return;
        onClose?.();
    };

    const handleConfirm = () => {
        const height = Number(String(heightMm).replace(",", "."));
        if (!Number.isFinite(height) || height < 1) {
            setHeightError("Толщина должна быть ≥ 1 мм");
            return;
        }

        const holesThrough = holesMode === HOLES_THROUGH;
        let depthInput = Number(String(holeDepthMm).replace(",", "."));
        if (hasHoles && !holesThrough) {
            if (!Number.isFinite(depthInput) || depthInput < 0.1) {
                setHoleDepthError("Глубина должна быть ≥ 0.1 мм");
                return;
            }
            const maxBlind = Math.max(height - 1, 0.1);
            if (depthInput >= height - 0.5) {
                setHoleDepthError(`Для глухих укажите меньше ${height} мм (макс. ${maxBlind})`);
                return;
            }
            depthInput = Math.min(depthInput, maxBlind);
        }

        onConfirm?.({
            name: String(name || "").trim() || "DXF деталь",
            position: String(position || "").trim(),
            heightMm: height,
            holesThrough: hasHoles ? holesThrough : true,
            holeDepthMm: hasHoles && !holesThrough ? depthInput : height,
        });
    };

    const canConfirm = isEdit
        ? !busy
        : Boolean(meta) && !parseError && !busy;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            PaperProps={{
                style: {
                    minWidth: 400,
                    borderRadius: 8,
                },
            }}
        >
            <DialogTitle textAlign="center">
                {isEdit ? "Редактировать деталь" : "Импорт DXF"}
            </DialogTitle>
            <DialogContent sx={{padding: "13px 24px", minWidth: 350}}>
                {!isEdit && parseError ? (
                    <Typography color="error" textAlign="center" sx={{mt: 1}}>
                        {parseError}
                    </Typography>
                ) : (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 1.5, mt: 0.5}}>
                        {previewMeta && (
                            <Typography variant="body2" color="text.secondary">
                                Габариты: {Math.round(previewMeta.length)} × {Math.round(previewMeta.width)} мм
                                {previewMeta.holeCount != null && (
                                    <>
                                        {" · "}
                                        отверстий: {previewMeta.holeCount}
                                    </>
                                )}
                                {previewMeta.contourSource && (
                                    <>
                                        {" · "}
                                        контур: {previewMeta.contourSource === "polyline" ? "полилиния" : "прямоугольник (AABB)"}
                                    </>
                                )}
                            </Typography>
                        )}
                        <TextField
                            label="Позиция"
                            size="small"
                            fullWidth
                            value={position}
                            onChange={(e) => setPosition(e.target.value)}
                            disabled={busy}
                            autoFocus
                        />
                        <TextField
                            label="Наименование"
                            size="small"
                            fullWidth
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            disabled={busy}
                        />
                        <TextField
                            label="Толщина, мм"
                            size="small"
                            fullWidth
                            value={heightMm}
                            onChange={(e) => {
                                setHeightMm(e.target.value);
                                setHeightError("");
                                setHoleDepthError("");
                            }}
                            error={Boolean(heightError)}
                            helperText={
                                heightError
                                || (isEdit ? "" : "В DXF толщины обычно нет — задайте вручную")
                            }
                            disabled={busy}
                        />
                        {hasHoles && (
                            <>
                                <FormControl disabled={busy}>
                                    <FormLabel sx={{fontSize: "0.85rem", mb: 0.5}}>Отверстия</FormLabel>
                                    <RadioGroup
                                        row
                                        value={holesMode}
                                        onChange={(e) => {
                                            setHolesMode(e.target.value);
                                            setHoleDepthError("");
                                        }}
                                    >
                                        <FormControlLabel
                                            value={HOLES_THROUGH}
                                            control={<Radio size="small"/>}
                                            label="Сквозные"
                                        />
                                        <FormControlLabel
                                            value={HOLES_BLIND}
                                            control={<Radio size="small"/>}
                                            label="Глухие"
                                        />
                                    </RadioGroup>
                                </FormControl>
                                {holesMode === HOLES_BLIND && (
                                    <TextField
                                        label="Глубина отверстий, мм"
                                        size="small"
                                        fullWidth
                                        value={holeDepthMm}
                                        onChange={(e) => {
                                            setHoleDepthMm(e.target.value);
                                            setHoleDepthError("");
                                        }}
                                        error={Boolean(holeDepthError)}
                                        helperText={
                                            holeDepthError
                                            || "Должна быть меньше толщины панели"
                                        }
                                        disabled={busy}
                                    />
                                )}
                            </>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{px: "1.25rem", py: 1.5, gap: 1}}>
                <ButtonExit
                    text="Отменить"
                    onClick={handleClose}
                    width={96}
                    sx={{
                        height: 40,
                        fontSize: "14px",
                        ...(busy ? {pointerEvents: "none", opacity: 0.5} : null),
                    }}
                />
                {(isEdit || !parseError) && (
                    <ButtonAdd
                        text={isEdit ? "Сохранить" : "Добавить"}
                        onClick={handleConfirm}
                        disabled={!canConfirm}
                        loading={busy}
                        width={96}
                        height={40}
                        fontSize="14px"
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};

export default DxfImportDialog;
